import {
  Box,
  Button,
  Grid,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { isEmpty, reject } from "lodash";
import React, { useState, useCallback, useEffect, useContext } from "react";
import { useDropzone } from "react-dropzone";
import CloseIcon from "@mui/icons-material/Close";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../services/models/AlertType";
import { SimpelPdfViwer } from "./SimpelPdfViwer";

interface MultiUploadProps {
  onDropandSelect: any;
  editFile: any;
  onFileRemove: any;
  onFileDownload: any;
  MAX_COUNT: number;
}

const allowedFileTypes = [
  // excel >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/msexcel",
  "application/x-msexcel",
  "application/x-ms-excel",
  "application/x-excel",
  "application/x-dos_ms_excel",
  "application/xls",
  "application/x-xls",
  "application/x-msi",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

  //CSV>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  ".csv",
  "text/csv",

  "application/pdf",
  "application/json",
  "image/jpeg",
  "image/png",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const MultiUpload: React.FC<MultiUploadProps> = ({
  onDropandSelect,
  editFile,
  onFileRemove,
  onFileDownload,
  MAX_COUNT,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [file, setFile] = useState<any>([]);
  const [fileLimit, setFileLimit] = useState(false);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [previewDocDialog, setPreviewDocDialog] = React.useState(false);
  const [previewDoc, setPreviewDoc] = React.useState<any>([]);

  // set file if alreday exists
  useEffect(() => {
    setFile(
      editFile.map((file:any) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
    // setFile(editFile)
  }, [editFile]);

  // on select or on drop file or image
  const onDrop = useCallback(
    (acceptedFiles:any) => {
      let limitExceeded = false;
      const uploaded = [...file];
      const checkForFile: any = [...file, ...acceptedFiles];
      if (
        file?.length === MAX_COUNT ||
        checkForFile === MAX_COUNT ||
        checkForFile > MAX_COUNT ||
        acceptedFiles > MAX_COUNT
      ) {
        return updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: `You can only add a maximum of ${MAX_COUNT} files`,
          alertType: AlertType.Error,
        });
      }
      acceptedFiles.some((file) => {
        if (uploaded.findIndex((f) => f.name === file.name) === -1) {
          uploaded.push(file);
          if (uploaded.length === MAX_COUNT) setFileLimit(true);
          if (uploaded.length > MAX_COUNT) {
            updateUserContext({
              ...userContext,
              isAlert: true,
              alertMessage: `You can only add a maximum of ${MAX_COUNT} files`,
              alertType: AlertType.Error,
            });
            setFileLimit(false);
            limitExceeded = true;
            return true;
          }
        }
      });
      // const filesWithId = uploaded.map((item, index) => {  //just edit the file but it's NIU -NOT IN USE
      //     return {
      //         id: index + 1,
      //         lastModified: item.lastModified,
      //         lastModifiedDate: item.lastModifiedDate,
      //         name: item.name,
      //         size: item.size,
      //         type: item.type,
      //         webkitRelativePath: item.webkitRelativePath
      //     };
      // });
      // console.log("filesWithId", filesWithId);
      // console.log("uploaded", uploaded);

      if (!limitExceeded) {
        setFile(
          uploaded.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          )
        );
        setFile(uploaded);
        onDropandSelect(uploaded);
      }
    },
    [file]
  );
  const customvalidator: any = (file) => {
    const MaxSize = 4;
    if (file.size / 1024 / 1204 > MaxSize) {
      return {
        code: "size-too-large",
        message: `Max allowed file size is 4 MB.`,
      };
    }
    if (!allowedFileTypes.includes(file.type)) {
      return {
        code: "invalid-type",
        message: `Please upload only Excel Files (supported types: xlsx, xls or csv only).`,
      };
    }
    return null;
  };
  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    validator: customvalidator,
    multiple: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/json": [".json"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/gif": [".gif"],
      "image/png": [".png"],
      "text/plain": [".txt"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    maxFiles: 5,
  });

  // errors on file uploadings
  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
    <Box>
      {errors.map((e) => (
        <span key={e.code} style={{ color: "red" }}>
          * {e.message}
        </span>
      ))}
    </Box>
  ));

  const getFormattedFileSize = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    // JV
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const img = {
    width: "-webkit-fill-available",
  };

  const previewDocDialogBox = (previewFile:any) => {
    setPreviewDocDialog(true);
    setPreviewDoc([previewFile]);
  };

  const handleCloseFilePreviewDialogBox = (val: any) => {
    setPreviewDocDialog(false);
  };

  return (
    <>
      {(file?.length <= 0 || MAX_COUNT !== 1) && (
        <>
          <Button
            {...getRootProps({ className: "dropzone" })}
            sx={{
              border: "dashed gray 1px !important",
              borderRadius: "6px",
              padding: "0px !important",
              "&:hover": {
                cursor: "grab",
              },
            }}
            size="medium"
          >
            <input {...getInputProps()} />
            <Box
              sx={{ justifyContent: "center" }}
              className="multi-upload-button"
            >
              <Box className="left-icon">
                <DriveFolderUploadIcon />
              </Box>
              <Box style={{ paddingTop: "2px", textAlign: "center" }}>
                Upload File
              </Box>
            </Box>
          </Button>
          {/* <br /> */}
          <span style={{ fontSize: "x-small", display: "block", marginTop: "6px" }}>
            {/* You can upload a maximum of {MAX_COUNT} files, 4MB each{" "} */}
            Maximum 5 file(s), 4MB each
          </span>
        </>
      )}
      {/* {
        !isEmpty(file) && ( */}
      <Box sx={{ marginTop: "6px" }}>
        {file?.length > 0 &&
          file?.map((files: any, index) => {
            return (
              <Box key={index}>
                <Grid
                  sx={{
                    alignItems: "start",
                    display: "flex",
                    backgroundColor: "transparent",
                    gap: 1
                  }}
                >
                  {["image/jpeg", "image/png", "application/pdf"].includes(files?.type) ? (
                    <span
                      className="multi-upload-right-icon"
                      onClick={() => previewDocDialogBox(files)}
                    >
                      <RemoveRedEyeIcon sx={{ color: "blue !important" }} />
                    </span>
                  ) : (
                    <span className="multi-upload-right-icon">
                      <Tooltip
                        title={"You can only view JPEG & PNG"}
                        placement="right"
                      >
                        <VisibilityOffIcon sx={{ color: "red !important" }} />
                      </Tooltip>
                    </span>
                  )}

                  {/* (for file name and size) - DP */}
                  {/* <div  >{previewsDocs}</div> */}
                  {/* <Button
                    sx={{
                      justifyContent: "start",
                      maxWidth: "500px",
                      minWidth: "240px !important",
                      textOverflow: "ellipsis ",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textAlign: "left",
                      display: "block",
                    }}
                    className="multi-upload-button"
                    onClick={() => {
                      onFileDownload(files);
                    }}
                  >
                    {files?.name} ({getFormattedFileSize(files?.size)})
                  </Button> */}

                  <Button
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      maxWidth: "450px",
                      minWidth: "240px !important",
                      overflow: "hidden",
                      textTransform: "none",
                      textAlign: "left",
                      paddingRight: "8px",
                    }}
                    className="multi-upload-button"
                    onClick={() => onFileDownload(files)}
                  >
                    <Tooltip
                      title={`${files?.name || ""} (${getFormattedFileSize(files?.size)})`}
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          flex: 1,
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        {files?.name}
                      </Box>
                    </Tooltip>
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        marginLeft: "4px",
                      }}
                    >
                      ({getFormattedFileSize(files?.size)})
                    </Box>
                  </Button>

                  <span
                    className="multi-upload-right-icon"
                    onClick={() => onFileRemove(files)}
                  >
                    <CloseIcon sx={{ color: "red !important" }} />
                  </span>
                </Grid>
              </Box>
            );
          })}
      </Box>
      {/* //   )
      // } */}
      <Box>{fileRejectionItems}</Box>
      {previewDoc?.length > 0 && (
        <Dialog
          open={previewDocDialog}
          onClose={handleCloseFilePreviewDialogBox}
          scroll={"paper"}
          sx={{ "& .MuiPaper-root": { maxWidth: "revert", width: 1000 } }}
          maxWidth="md"
          fullScreen={fullScreen}
        >
          <DialogTitle className="pdf-viewr-simple">
            <Grid
              sx={{
                display: "flew",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4>
                <b> {previewDoc[0]?.name}</b>
              </h4>
              <Tooltip title={"Close"} placement="top">
                <IconButton
                  aria-label="close"
                  onClick={handleCloseFilePreviewDialogBox}
                  style={{ marginTop: "-10px !important" }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </DialogTitle>
          <DialogContent
            sx={{
              marginTop: "5px",
              padding:
                previewDoc?.length > 0 &&
                  previewDoc[0]?.type === "application/pdf"
                  ? "0px !important"
                  : "",
            }}
            dividers
          >
            <Box>
              {previewDoc?.length > 0 && ['application/pdf'].includes(previewDoc[0]?.type) &&
                <SimpelPdfViwer
                  title={previewDoc[0]?.name}
                  source={previewDoc[0]?.preview}
                />}

              {previewDoc?.length > 0 &&
                ["image/jpeg", "image/png"].includes(previewDoc[0]?.type) && (
                  <img src={previewDoc[0]?.preview} style={img} alt="Img" />
                )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
